/**
 * Marketing consent (B8) — service layer.
 *
 * Marketing consent is the same append-only primitive as biometric, terms and
 * age: `consent_records` against a versioned policy row. A withdrawal is a NEW
 * entry, never an overwrite, so the history of what somebody agreed to and
 * when survives intact.
 *
 * Resend is a downstream MIRROR of that ledger, never the source of truth. The
 * sync is queued server-side by a trigger; `requestMarketingSync` only nudges
 * the worker so the round trip is immediate. If the nudge fails, the queue and
 * the paging sweep still carry it — nothing is lost by a failed nudge.
 */
import { supabase } from "@/integrations/supabase/client";

export interface MarketingConsentState {
  consented: boolean;
  known: boolean;
  recordedAt?: string | null;
}

/** Reads the caller's own current preference (latest ledger entry wins). */
export async function getMyMarketingConsent(): Promise<MarketingConsentState> {
  const { data, error } = await supabase.rpc("get_my_marketing_consent");
  const row = (data ?? {}) as Record<string, unknown>;
  if (error) return { consented: false, known: false };
  return {
    consented: row.consented === true,
    known: row.known === true,
    recordedAt: (row.recorded_at as string) ?? null,
  };
}

/**
 * Records an explicit opt-in or withdrawal.
 * `token` carries the guest session for the pre-account funnel; signed-in
 * callers can pass null and the row lands on the user scope.
 */
export async function recordMarketingConsent(
  action: "granted" | "withdrawn",
  token: string | null = null,
) {
  return supabase.rpc("record_marketing_consent", {
    p_token: token ?? "",
    p_action: action,
  });
}

/** Best-effort nudge of the Resend sync worker for the caller's own jobs. */
export async function requestMarketingSync(): Promise<void> {
  try {
    await supabase.functions.invoke("sync-marketing-contact", { body: {} });
  } catch {
    // Queue + sweep own durability. A failed nudge is not a failed sync.
  }
}

/** Sets the preference and pushes the mirror in one call. */
export async function setMarketingPreference(consented: boolean) {
  const res = await recordMarketingConsent(consented ? "granted" : "withdrawn");
  if (!res.error) await requestMarketingSync();
  return res;
}
